import { useMutation } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import axios from '@/plugins/axios'
import { computed, ref } from 'vue'
import router from '@/router'
import { useToast } from 'vue-toastification'
import { permissions } from '@/services/ability'
import type { ApiResponse, User } from '@/types/global'

type JsonRpcEnvelope<T> = ApiResponse<{ status: 'success' | 'error'; result: T }>

const requestOtpByPhone = async (phone: string) => {
  const response = await axios.post<
    JsonRpcEnvelope<{ phone: string } | string> | { error: { code: number; message: string } }
  >('/api/web/session/phone/authenticate', {
    jsonrpc: '2.0',
    params: { phone: phone, db: import.meta.env.VITE_API_DB }
  })

  return response.data
}

const authenticateWithOtp = async (phone: string, otp: string) => {
  const response = await axios.post<JsonRpcEnvelope<User>>('/api/web/session/phone/authenticate', {
    jsonrpc: '2.0',
    params: { phone: phone, db: import.meta.env.VITE_API_DB, otp: otp }
  })
  return response.data
}

const fetchCurrentUser = async () => {
  const response = await axios.post<ApiResponse<User>>('/api/web/session/get_session_info', {
    jsonrpc: '2.0',
    params: {}
  })
  return response.data
}

class AuthStore {
  user = ref<User | null>()

  init = async () => {
    try {
      const response = await fetchCurrentUser()
      this.user.value = response.result
    } catch (error) {
      this.user.value = null
    }
  }

  ability = computed(() => {
    return permissions(this.user.value)
  })

  useRequestOtp = (
    handleSuccess: (phone: string) => void,
    handleError?: (message: string) => void
  ) =>
    useMutation({
      mutationKey: ['request-otp'],
      retry: 3,
      mutationFn: async ({ phone }: { phone: string }) => {
        try {
          const data = await requestOtpByPhone(phone)
          return data
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async (data) => {
        const toast = useToast()

        if ('error' in data) {
          handleError?.(data.error.message)
          // toast.error('حدث خطأ عند إرسال رمز التحقق')
          return
        }

        if (data.result?.status === 'error') {
          const message = typeof data.result.result === 'string'
            ? data.result.result
            : 'حدث خطأ عند إرسال رمز التحقق'
          handleError?.(message)
          toast.error(message)
          return
        }

        if (data.result?.status === 'success' && typeof data.result.result !== 'string') {
          handleSuccess(data.result.result.phone)
          toast.success('تم إرسال رمز التحقق بنجاح')
          return
        }

        toast.error('حدث خطأ عند إرسال رمز التحقق')
      },
      onError: async () => {
        const toast = useToast()
        handleError?.('حدث خطأ ما، يرجى المحاولة لاحقاً')
        toast.error('حدث خطأ ما، يرجى المحاولة لاحقاً')
      }
    })

  useLogin = (handleError?: (message: string) => void) =>
    useMutation({
      mutationKey: ['login'],
      retry: 3,
      mutationFn: async ({ phone, otp }: { phone: string; otp: string }) => {
        try {
          const data = await authenticateWithOtp(phone, otp)
          return data
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async (data) => {
        const toast = useToast()

        if (data.result?.status === 'error') {
          const message = typeof data.result.result === 'string'
            ? data.result.result
            : 'حدث خطأ عند تسجيل الدخول'
          handleError?.(message)
          toast.error(message)
          return
        }

        if (data.result?.status === 'success') {
          this.user.value = data.result.result
          localStorage.user = JSON.stringify(data.result.result)
          sessionStorage.removeItem('otp_phone')
          sessionStorage.removeItem('otp_last_sent')
          sessionStorage.removeItem('otp_sent')
          localStorage.removeItem('selected-session-id')

          await router.push({ name: 'home' })
          toast.success('تم تسجيل الدخول بنجاح')
          return
        }

        handleError?.('حدث خطأ عند تسجيل الدخول')
        toast.error('حدث خطأ عند تسجيل الدخول')
      },
      onError: async () => {
        const toast = useToast()
        handleError?.('حدث خطأ ما، يرجى المحاولة لاحقاً')
        toast.error('حدث خطأ ما، يرجى المحاولة لاحقاً')
      }
    })

  useLogout = () =>
    useMutation({
      mutationKey: ['logout'],
      mutationFn: async () => {
        try {
          const response = await axios.post('/api/web/session/destroy', {
            jsonrpc: '2.0',
            params: {}
          })
          const result = response.data
          if (result) {
            localStorage.clear()
            router.push({ name: 'home' })
          }
        } catch (error) {
          return Promise.reject(error)
        }
      },
      onSuccess: async () => {
        const toast = useToast()
        await router.push({ name: 'login' })
        toast.success('تم تسجيل الخروج بنجاح')
      },
      onError: async () => {
        const toast = useToast()
        toast.error('حدث خطأ عند تسجيل الخروج')
      }
    })
}

export const auth = defineStore('auth', () => new AuthStore())
